
import { t } from "i18next";
import { CONST } from "models/constants";
import { MdRefresh } from "react-icons/md";
import GenericCard from "widgets/cards/generic";
import CircleButton from "widgets/controls/buttons/circle-button";
import { Alert, Timeline } from "api/types/alerts";
import TimelineComponent from "components/timeline/Timeline";
import { formatDate } from "util/datelib";
import { useState } from "react";
import GenericButton from "widgets/controls/buttons/generic";

interface GeneralProps {
  alert?: Alert;
  onUpdate?: () => void;
  onEventClick?: (event: Timeline) => void;
}

const AlertTimeline: React.FC<GeneralProps> = ({ alert, onUpdate, onEventClick }) => {
  const [showAllEvents, setShowAllEvents] = useState(true);
  
  const handleRefresh = async () => {
    if (onUpdate) {
      await onUpdate();
    }
  };

  const getTimelineStats = () => {
    if (!alert?.timeline || alert.timeline.length === 0) return null;
    
    const events = alert.timeline;
    const actions = events.reduce((acc, event) => {
      acc[event.action] = (acc[event.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostCommonAction = Object.entries(actions)
      .sort(([,a], [,b]) => (b as number) - (a as number))[0];

    return {
      totalEvents: events.length,
      mostCommonAction: mostCommonAction?.[0] || 'N/A',
      latestEvent: events[0]?.created,
      uniqueUsers: new Set(events.map(e => e.username)).size
    };
  };

  const stats = getTimelineStats();

  return (
    <>
    <GenericCard
      title={t("alert.timeline.title", "Timeline")}
      description={
        stats 
          ? `${stats.totalEvents} events • ${stats.uniqueUsers} users • Latest: ${stats.latestEvent ? formatDate(stats.latestEvent, 'lastcomms') : 'N/A'}`
          : t("alert.timeline.no.events", "No timeline events available")
      }
      bgColor="900"
      extraTitle="heading-md h-fit"
      topRight={
        <div className="-mt-2 flex items-center">
          <GenericButton
            text={showAllEvents ? t('timeline.filterNotifications', 'Filter') : t('timeline.showAll', 'Show All')}
            onClick={() => setShowAllEvents(!showAllEvents)}
            size="xs"
            rounded="rounded-md"
            variant="outline"
          />
              <CircleButton
            icon={MdRefresh}
            size="md"
            onClick={handleRefresh}
            tooltip={t('tooltip.refresh.timeline')}
            tooltipPlacement="bottom"
            variant="ghost"
            permissionRole={CONST.ROLE_OWNER}
          />
        </div>
      }
    >
      {alert?.timeline && alert.timeline.length > 0 ? (
        <div className="h-full pt-2">
          <TimelineComponent 
            events={alert.timeline}
            className="h-full"
            onEventClick={onEventClick}
            showAllEvents={showAllEvents}
          />
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-secondary">
            <div className="text-4xl mb-2">📅</div>
            <div className="text-lg font-medium mb-2">{t("alert.timeline.empty.title", "No Timeline Events")}</div>
            <div className="text-sm">{t("alert.timeline.empty.description", "This alert doesn't have any timeline events yet.")}</div>
          </div>
        </div>
      )}
    </GenericCard>
    </>
  );
};

export default AlertTimeline;
